import {
  cloneElement, createElement,
  ReactElement,
  ComponentType
} from "react";
import { ColumnDef } from "@tanstack/react-table";
import { TableCellProps } from "../components/TableCell";
import { TableHeader, TableHeaderProps } from "../components/TableHeader";

export const isReactElement = (children: ReactElement | boolean):
  children is ReactElement => Object.keys(children).includes("props");

export const isTableCell = <T>(children: ReactElement | boolean):
  children is ReactElement<TableCellProps<T, unknown>> =>
    isReactElement(children) &&
    Object.keys(children.props).includes("accessorFn");

export const isTableHeader = <T>(children: ReactElement | boolean):
  children is ReactElement<TableHeaderProps<T>> =>
    isReactElement(children) &&
    Object.keys(children.props).includes("header") &&
    Object.keys(children.props).includes("children");

type ReactElementWithChildren = ReactElement & {children: ReactElement};

export const isReactElementWithChildren =
  (children: ReactElement | ReactElementWithChildren):
  children is ReactElementWithChildren =>
    Object.keys(children.props).includes("children");

export const getColumnDefs = <T>(children: ReactElement<T> | boolean
  | (ReactElement<T> | boolean)[]):
  ColumnDef<T>[] => {
  if (Array.isArray(children)) {
    return children.map(getColumnDefs)
      .reduce((a, b) => a?.concat(b ?? []), []);
  } else {
    let columnDefs: ColumnDef<T>[] = [];
    if (isTableHeader<T>(children)) {
      columnDefs.push({
        id: children.props.header,
        header: () => cloneElement(children, {...children.props}),
        columns: getColumnDefs<T>((children.props.children ?? []) as ReactElement<T>[])
          ?.filter(d => !!d),
      });
    } else if (isTableCell<T>(children)) {
      columnDefs.push({
        ...children.props,
        id: children.props.id ?? children.props.header ?? "",
        header: (info) => children.props.headerRender
          ? children.props.headerRender(info)
          : createElement(
            TableHeader as unknown as ComponentType<TableHeaderProps<T>>,
            {
              header: children.props.header ?? "",
              align: children.props.align,
            }
          ),
        cell: info => children.props.render
          ? (children.props.render(info) ?? null)
          : cloneElement(children, {...info, ...children.props}),
      });
    } else if (isReactElement(children) && isReactElementWithChildren(children)) {
      columnDefs = columnDefs.concat(getColumnDefs(children.props.children));
    }

    return columnDefs;
  }
};

export const filterRowsByText = <T>(rows: T[], text: string, accessors?: Array<(row: T) => unknown>): T[] => {
  const needle = text?.trim().toLowerCase();
  if (!needle) return rows;

  const access = accessors?.length
    ? accessors
    : [
      (row: T) => row,
    ];

  return rows.filter(row => {
    return access.some(fn => {
      const value = fn(row);
      if (value == null) return false;
      if (Array.isArray(value)) {
        return value.some(v => String(v ?? "").toLowerCase().includes(needle));
      }
      if (typeof value === "object") {
        return JSON.stringify(value).toLowerCase().includes(needle);
      }
      return String(value ?? "").toLowerCase().includes(needle);
    });
  });
};
